/**
 * BulkIntelligencePanel.jsx
 * ==========================
 * Bulk AI processing for multiple documents.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { 
  Layers, Play, Pause, RefreshCw, FileText, 
  CheckCircle2, XCircle, Clock, Loader2, 
  Brain, Search, Tag, Check
} from 'lucide-react';
import { useCreditGate } from '../CreditGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OPERATIONS = [
  { id: 'ocr', label: 'OCR Extraction', icon: FileText, description: 'Extract text from images' },
  { id: 'metadata', label: 'Metadata Analysis', icon: Tag, description: 'Extract document metadata' },
  { id: 'summary', label: 'AI Summary', icon: Brain, description: 'Generate document summaries' },
  { id: 'entities', label: 'Entity Extraction', icon: Search, description: 'Extract entities (names, dates, amounts)' },
];

const BulkIntelligencePanel = ({ documents = [], onProcessComplete }) => {
  const { requestRun, dialog } = useCreditGate('bulk_intelligence', 'Confirm Bulk Intelligence');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectedOps, setSelectedOps] = useState(['ocr', 'metadata', 'summary']);
  const [processing, setProcessing] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localDocs, setLocalDocs] = useState([]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  // Handle documents prop or fetch if empty
  useEffect(() => {
    if (documents && documents.length > 0) {
      setLocalDocs(documents);
    } else {
      fetchDocuments();
    }
  }, [documents]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/documents/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const docs = Array.isArray(data) ? data : (data.documents || []);
        setLocalDocs(docs);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setLocalDocs([]);
    } finally {
      setLoading(false);
    }
  };

  // Use localDocs for display
  const displayDocs = localDocs.length > 0 ? localDocs : documents;

  useEffect(() => {
    let interval;
    if (batchId && processing) {
      interval = setInterval(() => {
        checkBatchStatus();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [batchId, processing]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/bulk-intelligence/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboard(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const startBulkProcess = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Please select at least one document');
      return;
    }
    if (selectedOps.length === 0) {
      toast.error('Please select at least one operation');
      return;
    }

    setProcessing(true);
    setBatchStatus(null);

    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/bulk-intelligence/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_ids: selectedDocs,
          operations: selectedOps,
          priority: 'normal'
        })
      });

      if (!response.ok) throw new Error('Failed to start batch');
      
      const data = await response.json();
      setBatchId(data.batch_id);
      toast.success('Batch processing started!');
    } catch (error) {
      console.error('Bulk process error:', error);
      toast.error('Failed to start batch processing');
      setProcessing(false);
    }
  };

  const checkBatchStatus = async () => {
    if (!batchId) return;

    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(
        `${API_URL}/api/agentic-extensions/bulk-intelligence/status/${batchId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const batch = data.batch || data;
        setBatchStatus(batch);
        
        if (batch.status === 'completed' || batch.status === 'failed') {
          setProcessing(false);
          fetchDashboard();
          if (batch.status === 'completed') {
            toast.success(`Batch processing completed! ${batch.processed_count || 0} documents processed.`);
            onProcessComplete?.();
          } else {
            toast.error('Batch processing failed');
          }
        }
      }
    } catch (error) {
      console.error('Status check error:', error);
    }
  };

  const toggleDoc = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const toggleOp = (opId) => {
    setSelectedOps(prev => 
      prev.includes(opId) 
        ? prev.filter(id => id !== opId)
        : [...prev, opId]
    );
  };

  const selectAll = () => {
    setSelectedDocs(displayDocs.map(d => d.id));
  };

  const clearSelection = () => {
    setSelectedDocs([]);
  };

  const progress = batchStatus 
    ? (batchStatus.total_documents > 0 
      ? Math.round((batchStatus.processed_count / batchStatus.total_documents) * 100) 
      : (batchStatus.status === 'completed' ? 100 : 0))
    : 0;

  return (
    <Card data-testid="bulk-intelligence-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" />
            <CardTitle>Bulk Intelligence</CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDashboard}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
        <CardDescription>
          Process multiple documents with AI in batch
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-4 gap-4">
            <div className="p-3 bg-muted/30 rounded-lg text-center">
              <div className="text-2xl font-bold">{dashboard.total_documents_processed || 0}</div>
              <div className="text-xs text-muted-foreground">Total Processed</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{Math.round(dashboard.success_rate || 0)}%</div>
              <div className="text-xs text-muted-foreground">Success Rate</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{dashboard.total_failed || 0}</div>
              <div className="text-xs text-muted-foreground">Failed</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{dashboard.recent_batches || 0}</div>
              <div className="text-xs text-muted-foreground">Batches</div>
            </div>
          </div>
        )}

        {/* Operations Selection */}
        <div>
          <label className="text-sm font-medium mb-3 block">Select Operations</label>
          <div className="grid grid-cols-2 gap-3">
            {OPERATIONS.map(op => {
              const Icon = op.icon;
              const isSelected = selectedOps.includes(op.id);
              return (
                <div
                  key={op.id}
                  onClick={() => toggleOp(op.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && toggleOp(op.id)}
                  className={`p-3 rounded-lg border text-left transition-all cursor-pointer ${
                    isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'bg-primary border-primary' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">{op.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    {op.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Document Selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium">Select Documents ({selectedDocs.length}/{displayDocs.length})</label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelection}>
                Clear
              </Button>
            </div>
          </div>
          
          <div className="border rounded-lg max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                Loading documents...
              </div>
            ) : displayDocs.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No documents available
              </div>
            ) : (
              displayDocs.map(doc => (
                <div
                  key={doc.id}
                  onClick={() => toggleDoc(doc.id)}
                  className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/30 transition-colors ${
                    selectedDocs.includes(doc.id) ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      selectedDocs.includes(doc.id) ? 'bg-primary border-primary' : 'border-gray-300'
                    }`}>
                      {selectedDocs.includes(doc.id) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium flex-1 truncate">{doc.title}</span>
                    <Badge variant="outline" className="text-xs">{doc.category || doc.doc_type || 'Document'}</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Progress */}
        {processing && batchStatus && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Processing...</span>
              <span className="font-medium">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                {(batchStatus.processed_count || 0) - (batchStatus.failed_count || 0)} completed
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-yellow-500" />
                {(batchStatus.total_documents || 0) - (batchStatus.processed_count || 0)} pending
              </span>
              <span className="flex items-center gap-1">
                <XCircle className="h-3 w-3 text-red-500" />
                {batchStatus.failed_count || 0} errors
              </span>
            </div>
          </div>
        )}

        {/* Results Display */}
        {!processing && batchStatus && batchStatus.status === 'completed' && batchStatus.results && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              Processing Complete - {batchStatus.processed_count || 0} of {batchStatus.total_documents || 0} documents
            </div>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {batchStatus.results.map((r, idx) => (
                <div key={idx} className={`p-3 rounded-lg border text-sm ${r.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{r.title || r.doc_id?.substring(0, 12)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {r.success ? 'Done' : 'Failed'}
                    </span>
                  </div>
                  {r.operations_completed && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Ops: {r.operations_completed.join(', ')}
                    </div>
                  )}
                  {r.error && <div className="text-xs text-red-600 mt-1">{r.error}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Process Button */}
        <Button 
          onClick={() => requestRun(startBulkProcess)} 
          disabled={processing || selectedDocs.length === 0 || selectedOps.length === 0}
          className="w-full"
          data-testid="start-bulk-process-btn"
        >
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing {selectedDocs.length} documents...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Process {selectedDocs.length} Documents
            </>
          )}
        </Button>
      </CardContent>
      {dialog}
    </Card>
  );
};

export default BulkIntelligencePanel;
